// @ts-nocheck
// =============================================================================
//  KitchenControl — Team Module
//  Props: { sb, tenantId, activeTab, onTabChange }
//  Gedeelde medewerkers store — ook gebruikt door HACCP
// =============================================================================
import { useState, useEffect, useRef, useCallback } from "react";
import { Users, X, Download, ChevronLeft, ChevronRight } from "lucide-react";

// ── Kleuren ───────────────────────────────────────────────────────────────────
import { useBeheerPin } from "./ui/BeheerPin";

const C = {
  bg:      "#F8FAFC",
  bg2:     "#FFFFFF",
  bg3:     "#F1F5F9",
  hdr:     "#0F172A",
  hdrBdr:  "#1E293B",
  txt:     "#0F172A",
  txt2:    "#475569",
  txt3:    "#94A3B8",
  bdr:     "#E2E8F0",
  bdr2:    "#CBD5E1",
  pri:     "#2563EB",
  priBg:   "#EFF6FF",
  priBd:   "#BFDBFE",
  green:   "#16A34A",
  greenBg: "#DCFCE7",
  red:     "#DC2626",
  redBg:   "#FEE2E2",
  redBd:   "#FCA5A5",
  amber:   "#D97706",
  amberBg: "#FFFBEB",
  amberBd: "#FDE68A",
};

const DAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const uid = () => `${Date.now()}-${Math.floor(Math.random() * 9999)}`;
const LS_KEY = "kc-team-v1";

// ── Avatar kleur op basis van naam ───────────────────────────────────────────
const AVATAR_KLEUREN = ["#2563EB","#7C3AED","#DB2777","#D97706","#16A34A","#0891B2","#DC2626"];
function avatarKleur(naam) {
  let hash = 0;
  for (let i = 0; i < (naam||"").length; i++) hash = naam.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_KLEUREN[Math.abs(hash) % AVATAR_KLEUREN.length];
}
function Avatar({ naam, size = 36 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: avatarKleur(naam), display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.42, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
      {(naam||"?")[0].toUpperCase()}
    </div>
  );
}

// Weergavenaam: voor- + achternaam, met terugval op het oude losse naam-veld
function naamVan(m) {
  const vol = `${m?.voornaam || ""} ${m?.achternaam || ""}`.trim();
  return vol || m?.naam || "?";
}

// ── ISO week helpers ──────────────────────────────────────────────────────────
function isoWeekOf(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const y = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return { year: d.getUTCFullYear(), week: Math.ceil(((d - y) / 86400000 + 1) / 7) };
}
function targetDate(dayIdx, weekOffset = 0) {
  const now = new Date();
  const cur = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const t = new Date(now);
  t.setDate(t.getDate() + (dayIdx - cur + weekOffset * 7));
  return t;
}
function weekDayKeysFor(offset) {
  return DAYS.map((d, i) => {
    const date = targetDate(i, offset);
    const { year, week } = isoWeekOf(date);
    return `${year}-W${String(week).padStart(2, "0")}-${d}`;
  });
}
function getWeekLabel(offset = 0) {
  const now = new Date();
  const cur = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const mon = new Date(now); mon.setDate(now.getDate() - cur + offset * 7);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const M = ["jan","feb","mrt","apr","mei","jun","jul","aug","sep","okt","nov","dec"];
  return `${mon.getDate()} ${M[mon.getMonth()]} – ${sun.getDate()} ${M[sun.getMonth()]}`;
}

// ── Uren berekening ───────────────────────────────────────────────────────────
function berekenDagUren(start, eind, pauze = 0) {
  if (!start || !eind) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = eind.split(":").map(Number);
  let min = eh * 60 + em - (sh * 60 + sm) - (pauze || 0);
  if (min <= 0) return 0;
  return Math.round(min / 15) * 0.25;
}
function weekTotaalUren(medewerker, offset) {
  const keys = new Set(weekDayKeysFor(offset));
  return (medewerker.uren || [])
    .filter(u => keys.has(u.dayKey))
    .reduce((sum, u) => sum + berekenDagUren(u.start, u.eind, u.pauze || 0), 0);
}

// ── Lokale opslag ─────────────────────────────────────────────────────────────
function loadLocal() {
  try { const s = localStorage.getItem(LS_KEY); if (s) return JSON.parse(s); } catch {}
  return null;
}
function saveLocal(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch {}
}

// ── Supabase helpers ──────────────────────────────────────────────────────────
async function sbLoad(sb, tenantId) {
  try {
    const rows = await sb.select("team_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
    if (rows?.length && rows[0]?.data) return rows[0].data;
  } catch (e) { console.warn("[Team] load failed:", e); }
  return null;
}
async function sbSave(sb, tenantId, data) {
  const updated_at = new Date().toISOString();
  try {
    const rows = await sb.select("team_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
    // VEILIGHEIDSSLOT: een lege medewerkerslijst mag NOOIT een gevulde lijst op de
    // server overschrijven. Voorkomt dataverlies bij een seed/lege staat.
    if (!data?.medewerkers?.length && rows?.length && rows[0]?.data?.medewerkers?.length) {
      console.warn("[Team] save geblokkeerd: lege lijst zou een gevulde lijst overschrijven.");
      return null;
    }
    if (rows?.length) {
      await sb.update("team_data", `tenant_id=eq.${tenantId}`, { data, updated_at });
    } else {
      await sb.insert("team_data", { tenant_id: tenantId, data, updated_at });
    }
    return updated_at;
  } catch (e) { console.warn("[Team] save failed:", e); return null; }
}

function initState() {
  const saved = loadLocal();
  if (saved?.medewerkers) return saved;
  return { medewerkers: [] };
}

// ─────────────────────────────────────────────────────────────────────────────
// TEAM MODULE
// ─────────────────────────────────────────────────────────────────────────────
export default function TeamModule({ sb, tenantId, activeTab, onTabChange, supabaseClient, userRole, userEmail }) {
  const { requireBeheer } = useBeheerPin();
  const magBeheren = userRole !== "medewerker"; // beheerder + manager mogen het rooster aanpassen
  const [state, setState] = useState(initState);
  const [syncStatus, setSyncStatus] = useState("local");
  const [urenMedewerker, setUrenMedewerker] = useState(null);
  const [urenWeekOffset, setUrenWeekOffset] = useState(0);
  const pollRef = useRef(null);

  const { medewerkers } = state;

  // ── Sync-laag (v2 patroon) ──────────────────────────────────────────────────
  const [synced, setSynced] = useState(false);
  const lastUpdatedAtRef = useRef(null);

  const update = useCallback((fn) => setState(s => {
    const next = { ...s, ...fn(s) };
    saveLocal(next);
    return next;
  }), []);

  // Remote data toepassen ZONDER een nieuwe save te triggeren (echo-guard)
  const applyingRemoteRef = useRef(false);
  const applyRemote = useCallback((data, stamp) => {
    if (!data?.medewerkers) return;
    applyingRemoteRef.current = true;
    lastUpdatedAtRef.current = stamp || null;
    setState(data);
    saveLocal(data);
  }, []);

  // 1. Load bij mount
  // Auto-opslag (effect 2) gaat PAS aan na een GESLAAGDE load. sb.select gooit geen
  // fout maar geeft [] bij mislukken — daarom: geeft de server NIETS terug terwijl er
  // lokaal wél medewerkers zijn, dan behandelen we dat als een mislukte load: niet
  // syncen, opnieuw proberen. Zo overschrijft een lege lijst nooit de echte.
  useEffect(() => {
    if (!sb || !tenantId) return;
    let cancelled = false;
    let pogingen = 0;
    const probeer = async () => {
      setSyncStatus("syncing");
      const rows = await sb.select("team_data", `tenant_id=eq.${tenantId}&select=data,updated_at&limit=1`);
      if (cancelled) return;
      const serverData = rows?.length ? rows[0]?.data : null;
      if (serverData?.medewerkers) {
        // Server gaf een bruikbare rij terug (ook een lege lijst telt) → toepassen + sync aan
        lastUpdatedAtRef.current = rows[0].updated_at;
        setState(serverData);
        saveLocal(serverData);
        setSyncStatus("synced");
        setSynced(true);
        return;
      }
      // Server gaf niets terug. Lokaal wél medewerkers? → verdacht (mislukte load).
      const lokaal = loadLocal();
      if (lokaal?.medewerkers?.length) {
        console.warn("[Team] load gaf niets terug terwijl er lokaal data is; opslaan uitgesteld, opnieuw proberen (poging " + (pogingen + 1) + ").");
        setSyncStatus("local");
        if (!cancelled && pogingen < 5) { pogingen++; setTimeout(probeer, 3000); }
        return;
      }
      // Geen serverdata én geen lokale data → echt (nog) leeg → sync mag aan.
      setSyncStatus("synced");
      setSynced(true);
    };
    probeer();
    return () => { cancelled = true; };
  }, [sb, tenantId]);

  // 2. Save bij wijziging — alleen als synced
  useEffect(() => {
    if (!synced) return;
    if (applyingRemoteRef.current) { applyingRemoteRef.current = false; return; } // remote apply: niet terugschrijven
    if (!sb || !tenantId) return;
    setSyncStatus("syncing");
    const t = setTimeout(async () => {
      const stamp = await sbSave(sb, tenantId, state);
      if (stamp) lastUpdatedAtRef.current = stamp;
      setSyncStatus("synced");
    }, 1200);
    return () => clearTimeout(t);
  }, [state, synced]);

  // 3. Poll elke 5 seconden
  useEffect(() => {
    if (!sb || !tenantId) return;
    const poll = async () => {
      if (!synced) return;
      try {
        const rows = await sb.select("team_data", `tenant_id=eq.${tenantId}&select=data,updated_at&limit=1`);
        if (!rows?.length || !rows[0].updated_at) return;
        if (rows[0].updated_at === lastUpdatedAtRef.current) return;
        if (rows[0].data) applyRemote(rows[0].data, rows[0].updated_at);
      } catch {}
    };
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [sb, tenantId, synced]);

  // ── 4. Supabase Realtime WebSocket voor instant sync ────────────────────────
  useEffect(() => {
    if (!supabaseClient || !tenantId || !synced) return;
    const channel = supabaseClient
      .channel(`team_data_${tenantId}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "team_data",
        filter: `tenant_id=eq.${tenantId}`,
      }, (payload) => {
        const remote = payload.new?.data;
        const stamp = payload.new?.updated_at || null;
        if (stamp && stamp === lastUpdatedAtRef.current) return; // eigen echo overslaan
        if (remote?.medewerkers) applyRemote(remote, stamp);     // breekt de save-lus
      })
      .subscribe();
    return () => supabaseClient.removeChannel(channel);
  }, [supabaseClient, tenantId, synced]);

  // ── CRUD medewerkers ─────────────────────────────────────────────────────────
  const voegToeMedewerker = async (data) => {
    if (!(await requireBeheer("een medewerker toe te voegen"))) return;
    const voornaam = (data.voornaam || "").trim();
    const achternaam = (data.achternaam || "").trim();
    const naam = `${voornaam} ${achternaam}`.trim();
    if (!naam) return;
    update(s => ({ medewerkers: [...s.medewerkers, {
      id: uid(), naam, voornaam, achternaam,
      telefoon: (data.telefoon || "").trim(),
      email: (data.email || "").trim(),
      functie: (data.functie || "").trim(),
      uren: [],
    }] }));
  };
  const updateMedewerker = async (id, data) => {
    if (!(await requireBeheer("medewerkergegevens te wijzigen"))) return;
    update(s => ({ medewerkers: s.medewerkers.map(m => {
      if (m.id !== id) return m;
      const voornaam = (data.voornaam ?? m.voornaam ?? "").trim();
      const achternaam = (data.achternaam ?? m.achternaam ?? "").trim();
      const naam = `${voornaam} ${achternaam}`.trim() || m.naam || "";
      return { ...m, ...data, voornaam, achternaam, naam };
    }) }));
  };
  const verwijder = async (id) => {
    if (!(await requireBeheer("een medewerker te verwijderen"))) return;
    if (!confirm(`Medewerker verwijderen? Alle uren-historie gaat ook verloren.`)) return;
    update(s => ({ medewerkers: s.medewerkers.filter(m => m.id !== id) }));
  };

  // ── Uren bijwerken ───────────────────────────────────────────────────────────
  const updateUren = async (medewerkerId, dayKey, partial) => {
    if (!(await requireBeheer("werktijden te wijzigen"))) return;
    update(s => ({
      medewerkers: s.medewerkers.map(m => {
        if (m.id !== medewerkerId) return m;
        const uren = Array.isArray(m.uren) ? [...m.uren] : [];
        const idx = uren.findIndex(u => u.dayKey === dayKey);
        if (idx >= 0) {
          const updated = { ...uren[idx], ...partial };
          if (!updated.start && !updated.eind && !updated.vrij) uren.splice(idx, 1);
          else uren[idx] = updated;
        } else {
          const nieuw = { dayKey, start: "", eind: "", pauze: 0, ...partial };
          if (nieuw.start || nieuw.eind || nieuw.vrij) uren.push(nieuw);
        }
        return { ...m, uren };
      })
    }));
  };

  // ── CSV download ─────────────────────────────────────────────────────────────
  const downloadCSV = (offset) => {
    const keys = weekDayKeysFor(offset);
    const { week, year } = isoWeekOf(targetDate(0, offset));
    const rows = [["Medewerker", ...DAYS, "Totaal"]];
    medewerkers.forEach(m => {
      const dagUren = keys.map((dk, i) => {
        const entry = (m.uren || []).find(u => u.dayKey === dk);
        return entry ? berekenDagUren(entry.start, entry.eind, entry.pauze || 0) : 0;
      });
      const totaal = dagUren.reduce((a, b) => a + b, 0);
      rows.push([m.naam, ...dagUren.map(u => u > 0 ? u.toFixed(2).replace(".", ",") : ""), totaal.toFixed(2).replace(".", ",")]);
    });
    const csv = rows.map(r => r.join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `uren-week${String(week).padStart(2,"0")}-${year}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // KC_UI_V2_TEAM_COMBINED: één Team-pagina, geen aparte onderdelen.
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: C.bg, fontFamily: "system-ui,-apple-system,sans-serif", fontSize: 13, color: C.txt, overflow: "hidden" }}>
      <header style={{ background: C.hdr, borderBottom: `1px solid ${C.hdrBdr}`, height: 52, display: "flex", alignItems: "center", padding: "0 18px", gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>👥 Team</span>
        <div style={{ flex: 1 }} />
        {sb && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: syncStatus === "syncing" ? "#FCD34D" : "rgba(255,255,255,.45)" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: syncStatus === "syncing" ? "#FCD34D" : "#4ADE80" }} />
            {syncStatus === "syncing" ? "synchroniseren…" : "gesynchroniseerd"}
          </div>
        )}
      </header>

      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ flex: "0 0 auto", maxHeight: 270, overflow: "hidden", borderBottom: `1px solid ${C.bdr}`, background: C.bg2 }}>
          <MedewerkersView
            medewerkers={medewerkers}
            onAdd={voegToeMedewerker}
            onUpdate={updateMedewerker}
            onDelete={verwijder}
            magBeheren={magBeheren}
            compact
          />
        </div>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <WerktijdenView medewerkers={medewerkers} onUpdateUren={updateUren} onDownload={downloadCSV} magBeheren={magBeheren} userEmail={userEmail} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MEDEWERKERS — beheer (info per persoon)
// ─────────────────────────────────────────────────────────────────────────────
function MedewerkersView({ medewerkers, onAdd, onUpdate, onDelete, magBeheren = true, compact = false }) {
  const [detail, setDetail] = useState(null); // medewerker (infovenster)
  const [form, setForm] = useState(null);     // {mode:"new"} | {mode:"edit", m}
  return (
    <div style={{ flex: compact ? "0 1 auto" : 1, overflowY: "auto", padding: compact ? "14px 18px" : 20 }}>
      <div style={{ maxWidth: compact ? "none" : 620, margin: "0 auto" }}>
        {compact && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.txt }}>Medewerkers</div>
              <div style={{ fontSize: 11.5, color: C.txt3, marginTop: 2 }}>{medewerkers.length} teamlid{medewerkers.length === 1 ? "" : "en"}</div>
            </div>
            {magBeheren && (
              <button onClick={() => setForm({ mode: "new" })} style={{ border: "none", borderRadius: 9, background: C.pri, color: "#fff", padding: "8px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>+ Medewerker toevoegen</button>
            )}
          </div>
        )}
        {medewerkers.length === 0 && (
          <div style={{ padding: "40px 0", textAlign: "center", color: C.txt3 }}>
            <Users size={36} strokeWidth={1} style={{ display: "block", margin: "0 auto 12px" }} />
            <p>Nog geen medewerkers. Voeg de eerste hieronder toe.</p>
          </div>
        )}
        <div style={{ display: compact ? "grid" : "flex", gridTemplateColumns: compact ? "repeat(auto-fit, minmax(230px, 1fr))" : undefined, flexDirection: compact ? undefined : "column", gap: compact ? 8 : 10, marginBottom: compact ? 0 : 16 }}>
          {medewerkers.map(m => (
            <div key={m.id} onClick={() => setDetail(m)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 12, cursor: "pointer", transition: "box-shadow .12s" }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,.07)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
              <Avatar naam={naamVan(m)} size={42} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: C.txt }}>{naamVan(m)}</div>
                <div style={{ fontSize: 12, color: C.txt3, marginTop: 2 }}>
                  {[m.functie, m.telefoon].filter(Boolean).join(" · ") || "Geen extra gegevens"}
                </div>
              </div>
              <span style={{ color: C.txt3, fontSize: 18 }}>›</span>
            </div>
          ))}
        </div>
        {!compact && magBeheren && <button onClick={() => setForm({ mode: "new" })} style={{ width: "100%", padding: "13px", borderRadius: 12, border: `1.5px dashed ${C.bdr2}`, background: C.bg2, color: C.pri, fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>+ Medewerker toevoegen</button>}
      </div>

      {detail && (
        <MedewerkerDetail
          m={detail}
          onBewerken={() => { setForm({ mode: "edit", m: detail }); setDetail(null); }}
          onVerwijder={() => { onDelete(detail.id); setDetail(null); }}
          onClose={() => setDetail(null)}
          magBeheren={magBeheren}
        />
      )}
      {form && (
        <MedewerkerPopup
          medewerker={form.mode === "edit" ? form.m : null}
          onSave={(data) => { if (form.mode === "edit") onUpdate(form.m.id, data); else onAdd(data); setForm(null); }}
          onClose={() => setForm(null)}
        />
      )}
    </div>
  );
}

// Infovenster (lezen) — open je door op een medewerker te klikken
function MedewerkerDetail({ m, onBewerken, onVerwijder, onClose, magBeheren = true }) {
  useEffect(() => { const h = e => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
  const rij = (label, waarde) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "11px 0", borderBottom: `1px solid ${C.bdr}` }}>
      <span style={{ fontSize: 12.5, color: C.txt3, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13.5, color: waarde ? C.txt : C.txt3, fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{waarde || "—"}</span>
    </div>
  );
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 400, padding: 22, boxShadow: "0 24px 60px -12px rgba(0,0,0,.45)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <Avatar naam={naamVan(m)} size={48} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.txt }}>{naamVan(m)}</div>
            {m.functie ? <div style={{ fontSize: 12.5, color: C.txt3 }}>{m.functie}</div> : null}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ marginBottom: 18 }}>
          {rij("Telefoon", m.telefoon)}
          {rij("E-mail", m.email)}
          {rij("Functie / rol", m.functie)}
        </div>
        {magBeheren && (
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onVerwijder} style={{ flex: 1, borderRadius: 10, padding: 12, fontSize: 13.5, fontWeight: 700, border: `1px solid ${C.redBd}`, background: C.redBg, color: C.red, cursor: "pointer", fontFamily: "inherit" }}>Verwijderen</button>
            <button onClick={onBewerken} style={{ flex: 2, borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 700, border: "none", background: C.pri, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Bewerken</button>
          </div>
        )}
      </div>
    </div>
  );
}


function MedewerkerPopup({ medewerker, onSave, onClose }) {
  const [f, setF] = useState({
    voornaam: medewerker?.voornaam || "",
    achternaam: medewerker?.achternaam || (medewerker && !medewerker.voornaam ? (medewerker.naam || "") : ""),
    telefoon: medewerker?.telefoon || "",
    email: medewerker?.email || "",
    functie: medewerker?.functie || "",
  });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const geldig = f.voornaam.trim() || f.achternaam.trim();
  useEffect(() => { const h = e => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);

  const inp = { width: "100%", boxSizing: "border-box", border: `1px solid ${C.bdr2}`, borderRadius: 9, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", color: C.txt, background: "#fff" };
  const lbl = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".4px", textTransform: "uppercase", color: C.txt2, marginBottom: 5 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 420, padding: 22, boxShadow: "0 24px 60px -12px rgba(0,0,0,.45)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
          <h3 style={{ flex: 1, margin: 0, fontSize: 17, fontWeight: 800, color: C.txt }}>{medewerker ? "Medewerker bewerken" : "Nieuwe medewerker"}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={lbl}>Voornaam</label><input style={inp} value={f.voornaam} onChange={e => set("voornaam", e.target.value)} autoFocus /></div>
          <div><label style={lbl}>Achternaam</label><input style={inp} value={f.achternaam} onChange={e => set("achternaam", e.target.value)} /></div>
          <div style={{ gridColumn: "1/3" }}><label style={lbl}>Telefoon</label><input style={inp} type="tel" value={f.telefoon} onChange={e => set("telefoon", e.target.value)} placeholder="06 12345678" /></div>
          <div style={{ gridColumn: "1/3" }}><label style={lbl}>E-mail</label><input style={inp} type="email" value={f.email} onChange={e => set("email", e.target.value)} placeholder="naam@voorbeeld.nl" /></div>
          <div style={{ gridColumn: "1/3" }}><label style={lbl}>Functie / rol</label><input style={inp} value={f.functie} onChange={e => set("functie", e.target.value)} placeholder="Bijv. Kok, Afwas, Bediening" /></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={{ flex: 1, borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 700, border: `1px solid ${C.bdr2}`, background: "#fff", color: C.txt2, cursor: "pointer", fontFamily: "inherit" }}>Annuleren</button>
          <button disabled={!geldig} onClick={() => onSave(f)} style={{ flex: 2, borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 700, border: "none", background: geldig ? C.pri : C.bdr2, color: "#fff", cursor: geldig ? "pointer" : "not-allowed", fontFamily: "inherit" }}>Opslaan</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WERKTIJDEN — rooster (medewerkers x dagen), tik op cel om in te vullen
// ─────────────────────────────────────────────────────────────────────────────
const MAAND = ["jan","feb","mrt","apr","mei","jun","jul","aug","sep","okt","nov","dec"];
const VOLLEDAG = ["Maandag","Dinsdag","Woensdag","Donderdag","Vrijdag","Zaterdag","Zondag"];

function WerktijdenView({ medewerkers, onUpdateUren, onDownload, magBeheren, userEmail }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [editCel, setEditCel] = useState(null);
  const [editEind, setEditEind] = useState(null);
  const eigenEmail = (userEmail || "").trim().toLowerCase();
  const keys = weekDayKeysFor(weekOffset);
  const { week } = isoWeekOf(targetDate(0, weekOffset));
  const datums = DAYS.map((d, i) => targetDate(i, weekOffset));

  const dagTotaal = keys.map(dk => medewerkers.reduce((sum, m) => {
    const e = (m.uren || []).find(u => u.dayKey === dk);
    return sum + (e ? berekenDagUren(e.start, e.eind, e.pauze || 0) : 0);
  }, 0));
  const weekTotaalAlles = dagTotaal.reduce((a, b) => a + b, 0);

  const navBtn = { width: 34, height: 34, border: `1px solid ${C.bdr}`, borderRadius: 8, background: "#fff", color: C.txt2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };
  const th = { background: C.bg, fontSize: 11, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: ".4px", padding: "9px 4px", textAlign: "center", borderBottom: `1px solid ${C.bdr}`, borderRight: `1px solid ${C.bdr}` };
  const naamcol = { textAlign: "left", padding: "8px 12px", minWidth: 158, position: "sticky", left: 0, background: C.bg2, borderRight: `1px solid ${C.bdr}`, borderBottom: `1px solid ${C.bdr}`, zIndex: 1 };
  const celKnop = (e) => {
    const vrij = e && e.vrij;
    const bezig = e && e.start && !e.eind;
    const leeg = !e || (!e.start && !e.eind && !e.vrij);
    return { width: "100%", minWidth: 74, minHeight: 48, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, padding: "6px 4px", lineHeight: 1.2,
      border: `1px solid ${bezig ? C.amberBd : C.bdr2}`, borderStyle: leeg ? "dashed" : "solid", background: bezig ? C.amberBg : (vrij ? C.bg3 : "#fff") };
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Weekbalk */}
      <div style={{ padding: "12px 18px 0", fontSize: 14, fontWeight: 800, color: C.txt }}>Werktijden</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 18px 12px", flexShrink: 0, flexWrap: "wrap" }}>
        <button onClick={() => setWeekOffset(o => o - 1)} style={navBtn}><ChevronLeft size={15} /></button>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Week {week} · {getWeekLabel(weekOffset)}</div>
        <button onClick={() => setWeekOffset(o => o + 1)} style={navBtn}><ChevronRight size={15} /></button>
        <div style={{ flex: 1 }} />
        <button onClick={() => onDownload(weekOffset)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: C.pri, background: C.priBg, border: `1px solid ${C.priBd}`, borderRadius: 8, padding: "8px 13px", cursor: "pointer", fontFamily: "inherit" }}><Download size={14} /> Exporteer (CSV)</button>
      </div>

      {medewerkers.length === 0 ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: C.txt3, padding: 20, textAlign: "center" }}>
          <Users size={38} strokeWidth={1} style={{ marginBottom: 12 }} />
          <p>Nog geen medewerkers. Voeg ze eerst toe onder <strong>Medewerkers</strong>.</p>
        </div>
      ) : (
        <div style={{ flex: 1, overflow: "auto", padding: "0 18px 18px" }}>
          <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...th, ...naamcol, background: C.bg, borderLeft: `1px solid ${C.bdr}`, zIndex: 2 }}>Medewerker</th>
                {DAYS.map((d, i) => (
                  <th key={d} style={th}>
                    <div style={{ fontSize: 13, color: C.txt, textTransform: "none" }}>{d}</div>
                    <div style={{ fontSize: 10, color: C.txt3, fontWeight: 600 }}>{datums[i].getDate()} {MAAND[datums[i].getMonth()]}</div>
                  </th>
                ))}
                <th style={th}>Totaal</th>
              </tr>
            </thead>
            <tbody>
              {medewerkers.map(m => {
                const wk = weekTotaalUren(m, weekOffset);
                return (
                  <tr key={m.id}>
                    <td style={naamcol}>
                      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                        <Avatar naam={naamVan(m)} size={30} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: C.txt, lineHeight: 1.2 }}>{naamVan(m)}</div>
                          {m.functie ? <div style={{ fontSize: 11, color: C.txt3, lineHeight: 1.2 }}>{m.functie}</div> : null}
                        </div>
                      </div>
                    </td>
                    {keys.map((dk, i) => {
                      const e = (m.uren || []).find(u => u.dayKey === dk);
                      const dagUren = e ? berekenDagUren(e.start, e.eind, e.pauze || 0) : 0;
                      const isEigen = !magBeheren && eigenEmail && (m.email || "").trim().toLowerCase() === eigenEmail;
                      const magEind = isEigen && e && e.start && !e.vrij; // eigen ingeroosterde dienst
                      const klikbaar = magBeheren || magEind;
                      const dagLabel = `${VOLLEDAG[i]} ${datums[i].getDate()} ${MAAND[datums[i].getMonth()]}`;
                      return (
                        <td key={dk} style={{ padding: "6px 6px", textAlign: "center", borderBottom: `1px solid ${C.bdr}`, borderRight: `1px solid ${C.bdr}` }}>
                          <button style={{ ...celKnop(e), cursor: klikbaar ? "pointer" : "default" }}
                            onClick={
                              magBeheren ? () => setEditCel({ medewerkerId: m.id, dayKey: dk, dagLabel, naam: naamVan(m), entry: e || null })
                              : magEind ? () => setEditEind({ medewerkerId: m.id, dayKey: dk, dagLabel, naam: naamVan(m), start: e.start, eind: e.eind || "", pauze: e.pauze || 0 })
                              : undefined
                            }>
                            {e && e.vrij
                              ? <span style={{ fontSize: 12.5, fontWeight: 700, color: C.txt3 }}>Vrij</span>
                              : (!e || (!e.start && !e.eind))
                                ? <span style={{ fontSize: 16, color: C.txt3 }}>{klikbaar ? "＋" : "·"}</span>
                                : (e.start && !e.eind)
                                  ? <span style={{ color: C.amber, fontWeight: 700 }}>{e.start}</span>
                                  : <span style={{ fontWeight: 700, color: C.txt }}>{e.start}–{e.eind}<span style={{ display: "block", fontSize: 10, color: C.txt3, fontWeight: 600 }}>{dagUren > 0 ? `${dagUren.toFixed(2).replace(".", ",")}u` : ""}</span></span>}
                          </button>
                        </td>
                      );
                    })}
                    <td style={{ textAlign: "center", fontWeight: 700, fontSize: 14, color: C.txt, borderBottom: `1px solid ${C.bdr}`, borderRight: `1px solid ${C.bdr}` }}>{wk > 0 ? wk.toFixed(2).replace(".", ",") : "—"}</td>
                  </tr>
                );
              })}
              <tr>
                <td style={{ ...naamcol, fontSize: 11, textTransform: "uppercase", letterSpacing: ".4px", color: C.txt3, fontWeight: 700, background: C.bg }}>Dagtotaal</td>
                {dagTotaal.map((t, i) => <td key={i} style={{ textAlign: "center", fontWeight: 700, color: C.txt2, background: C.bg, fontSize: 12, padding: "8px 4px", borderRight: `1px solid ${C.bdr}` }}>{t > 0 ? `${t.toFixed(2).replace(".", ",")}u` : "—"}</td>)}
                <td style={{ textAlign: "center", fontWeight: 800, color: C.txt, background: C.bg, fontSize: 14 }}>{weekTotaalAlles > 0 ? weekTotaalAlles.toFixed(2).replace(".", ",") : "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {editCel && (
        <CelEditor
          info={editCel}
          onSave={(vals) => { onUpdateUren(editCel.medewerkerId, editCel.dayKey, vals); setEditCel(null); }}
          onWis={() => { onUpdateUren(editCel.medewerkerId, editCel.dayKey, { start: "", eind: "", pauze: 0, vrij: false }); setEditCel(null); }}
          onClose={() => setEditCel(null)}
        />
      )}

      {editEind && (
        <EindtijdEditor
          info={editEind}
          onSave={(vals) => { onUpdateUren(editEind.medewerkerId, editEind.dayKey, vals); setEditEind(null); }}
          onClose={() => setEditEind(null)}
        />
      )}
    </div>
  );
}

function CelEditor({ info, onSave, onWis, onClose }) {
  const entry = info.entry || {};
  const [start, setStart] = useState(entry.start || "");
  const [eind, setEind] = useState(entry.eind || "");
  const [pauze, setPauze] = useState(entry.pauze ?? 0);
  useEffect(() => { const h = e => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);

  const nu = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
  const uren = berekenDagUren(start, eind, pauze);

  const lbl = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".4px", textTransform: "uppercase", color: C.txt2, marginBottom: 5 };
  const tijdInput = { width: "100%", boxSizing: "border-box", border: `1px solid ${C.bdr2}`, borderRadius: 10, padding: 12, fontSize: 20, fontWeight: 700, textAlign: "center", color: C.txt, fontFamily: "inherit", letterSpacing: 1 };
  const nuBtn = { marginTop: 6, width: "100%", border: `1px solid ${C.priBd}`, background: C.priBg, color: C.pri, borderRadius: 8, padding: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };
  const stepBtn = { width: 40, height: 44, border: `1px solid ${C.bdr2}`, borderRadius: 9, background: C.bg3, fontSize: 20, color: C.txt2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 380, padding: "22px 22px 20px", boxShadow: "0 24px 60px -12px rgba(0,0,0,.45)" }}>
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: "0 0 2px", fontSize: 17, fontWeight: 800, color: C.txt }}>{info.dagLabel}</h3>
            <div style={{ fontSize: 12.5, color: C.txt3, marginBottom: 18 }}>{info.naam}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, padding: 4 }}><X size={18} /></button>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Begin</label>
            <input type="time" style={tijdInput} value={start} onChange={e => setStart(e.target.value)} />
            <button style={nuBtn} onClick={() => setStart(nu())}>Nu</button>
          </div>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Eind</label>
            <input type="time" style={tijdInput} value={eind} onChange={e => setEind(e.target.value)} />
            <button style={nuBtn} onClick={() => setEind(nu())}>Nu</button>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Pauze (min)</label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button style={stepBtn} onClick={() => setPauze(p => Math.max(0, (parseInt(p) || 0) - 5))}>–</button>
            <input type="number" min="0" max="180" value={pauze} onChange={e => setPauze(e.target.value)} style={{ flex: 1, textAlign: "center", boxSizing: "border-box", border: `1px solid ${C.bdr2}`, borderRadius: 9, padding: 11, fontSize: 15, fontWeight: 700, fontFamily: "inherit", color: C.txt }} />
            <button style={stepBtn} onClick={() => setPauze(p => Math.min(180, (parseInt(p) || 0) + 5))}>+</button>
          </div>
        </div>

        <div style={{ fontSize: 13, color: C.txt2, marginBottom: 14 }}>Gewerkt: <strong style={{ color: C.txt, fontSize: 15 }}>{uren > 0 ? `${uren.toFixed(2).replace(".", ",")} uur` : "—"}</strong></div>

        <button onClick={() => onSave({ start: "", eind: "", pauze: 0, vrij: true })}
          style={{ width: "100%", marginBottom: 12, borderRadius: 10, padding: 11, fontSize: 13.5, fontWeight: 700, border: `1px solid ${C.bdr2}`, background: C.bg3, color: C.txt2, cursor: "pointer", fontFamily: "inherit" }}>
          Markeer als vrije dag
        </button>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onWis} style={{ flex: 1, borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 700, border: `1px solid ${C.bdr2}`, background: "#fff", color: C.txt2, cursor: "pointer", fontFamily: "inherit" }}>Wissen</button>
          <button onClick={() => onSave({ start, eind, pauze: parseInt(pauze) || 0, vrij: false })} style={{ flex: 2, borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 700, border: "none", background: C.pri, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Opslaan</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EINDTIJD-EDITOR — medewerker vult op z'n eigen dienst alleen eindtijd + pauze in
// (begintijd staat vast, ingepland door beheerder/manager). Stap: 15 minuten.
// ─────────────────────────────────────────────────────────────────────────────
function EindtijdEditor({ info, onSave, onClose }) {
  const delen = (info.eind && info.eind.includes(":")) ? info.eind.split(":") : ["", ""];
  let iU = delen[0] || "";
  let iM = delen[1] || "";
  if (iM) {
    let mm = Math.round((parseInt(iM) || 0) / 15) * 15;
    if (mm === 60) { mm = 0; iU = String(((parseInt(iU) || 0) + 1) % 24).padStart(2, "0"); }
    iM = String(mm).padStart(2, "0");
  }
  const [uur, setUur] = useState(iU);
  const [min, setMin] = useState(iM || "00");
  const [pauze, setPauze] = useState(info.pauze ?? 0);
  const [anders, setAnders] = useState(![0, 15, 30, 45].includes(info.pauze ?? 0));
  useEffect(() => { const h = e => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);

  const eind = uur ? `${uur}:${min}` : "";
  const uren = berekenDagUren(info.start, eind, pauze);

  const nuKwartier = () => {
    const d = new Date();
    let m = Math.round(d.getMinutes() / 15) * 15;
    let h = d.getHours();
    if (m === 60) { m = 0; h = (h + 1) % 24; }
    setUur(String(h).padStart(2, "0"));
    setMin(String(m).padStart(2, "0"));
  };

  const lbl = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".4px", textTransform: "uppercase", color: C.txt2, marginBottom: 5 };
  const sel = { flex: 1, boxSizing: "border-box", border: `1px solid ${C.bdr2}`, borderRadius: 10, padding: 12, fontSize: 18, fontWeight: 700, textAlign: "center", color: C.txt, fontFamily: "inherit", background: "#fff" };
  const pBtn = (actief) => ({ flex: 1, borderRadius: 9, padding: "10px 6px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${actief ? C.pri : C.bdr2}`, background: actief ? C.priBg : "#fff", color: actief ? C.pri : C.txt2 });
  const kiesPauze = (p) => { setPauze(p); setAnders(false); };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 380, padding: "22px 22px 20px", boxShadow: "0 24px 60px -12px rgba(0,0,0,.45)" }}>
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: "0 0 2px", fontSize: 17, fontWeight: 800, color: C.txt }}>{info.dagLabel}</h3>
            <div style={{ fontSize: 12.5, color: C.txt3, marginBottom: 18 }}>Jouw dienst</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, padding: 4 }}><X size={18} /></button>
        </div>

        <div style={{ display: "flex", gap: 16, marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Begin</label>
            <div style={{ ...sel, background: C.bg3, color: C.txt2 }}>{info.start || "—"}</div>
          </div>
          <div style={{ flex: 1.4 }}>
            <label style={lbl}>Eindtijd</label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <select value={uur} onChange={e => setUur(e.target.value)} style={sel}>
                <option value="">--</option>
                {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map(h => <option key={h} value={h}>{h}</option>)}
              </select>
              <span style={{ fontWeight: 800, color: C.txt2 }}>:</span>
              <select value={min} onChange={e => setMin(e.target.value)} style={sel}>
                {["00", "15", "30", "45"].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <button onClick={nuKwartier} style={{ marginTop: 6, width: "100%", border: `1px solid ${C.priBd}`, background: C.priBg, color: C.pri, borderRadius: 8, padding: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Klaar — nu</button>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Pauze gehad?</label>
          <div style={{ display: "flex", gap: 6 }}>
            <button style={pBtn(!anders && pauze === 0)} onClick={() => kiesPauze(0)}>Geen</button>
            <button style={pBtn(!anders && pauze === 15)} onClick={() => kiesPauze(15)}>15</button>
            <button style={pBtn(!anders && pauze === 30)} onClick={() => kiesPauze(30)}>30</button>
            <button style={pBtn(!anders && pauze === 45)} onClick={() => kiesPauze(45)}>45</button>
            <button style={pBtn(anders)} onClick={() => setAnders(true)}>Anders</button>
          </div>
          {anders && (
            <input type="number" min="0" max="180" value={pauze} onChange={e => setPauze(e.target.value)}
              style={{ marginTop: 8, width: "100%", boxSizing: "border-box", textAlign: "center", border: `1px solid ${C.bdr2}`, borderRadius: 9, padding: 11, fontSize: 15, fontWeight: 700, fontFamily: "inherit", color: C.txt }} placeholder="minuten pauze" />
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: C.txt2 }}>Gewerkt: <strong style={{ color: C.txt, fontSize: 15 }}>{uren > 0 ? `${uren.toFixed(2).replace(".", ",")} uur` : "—"}</strong></div>
          <div style={{ fontSize: 12, color: C.txt3, fontStyle: "italic" }}>MEP-lijst ingevuld? ;-)</div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => onSave({ eind: "", pauze: 0, vrij: false })} style={{ flex: 1, borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 700, border: `1px solid ${C.bdr2}`, background: "#fff", color: C.txt2, cursor: "pointer", fontFamily: "inherit" }}>Wis</button>
          <button disabled={!eind} onClick={() => onSave({ eind, pauze: parseInt(pauze) || 0, vrij: false })} style={{ flex: 2, borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 700, border: "none", background: eind ? C.pri : C.bdr2, color: "#fff", cursor: eind ? "pointer" : "not-allowed", fontFamily: "inherit" }}>Opslaan</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT: gedeelde medewerkers lezen vanuit andere modules (HACCP etc.)
// ─────────────────────────────────────────────────────────────────────────────
export function getMedewerkers() {
  try {
    const s = localStorage.getItem("kc-team-v1");
    if (s) return JSON.parse(s)?.medewerkers || [];
  } catch {}
  return [];
}
